import { useEffect, useRef, useState } from "react";

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
}

const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

const startOfDay = (d: Date) => {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
};

const toISO = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseISO = (s: string): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export default function DatePicker({ value, onChange }: DatePickerProps) {
  const today = startOfDay(new Date());
  const selected = parseISO(value);
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => selected ?? today);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const openPicker = () => {
    setViewDate(selected ?? today);
    setOpen((v) => !v);
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7; // Monday-first grid
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();

  const goPrevMonth = () => {
    if (isCurrentMonth) return;
    setViewDate(new Date(year, month - 1, 1));
  };
  const goNextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const selectDay = (d: Date) => {
    if (d.getTime() < today.getTime()) return;
    onChange(toISO(d));
    setOpen(false);
  };

  const displayLabel = selected
    ? `${String(selected.getDate()).padStart(2, "0")} ${MONTHS[selected.getMonth()]} ${selected.getFullYear()}`
    : "Tarih seçin";

  return (
    <div className="rr-datepicker" ref={wrapRef}>
      <button type="button" className="rr-datepicker-trigger" onClick={openPicker}>
        <span className={selected ? "" : "rr-datepicker-placeholder"}>{displayLabel}</span>
        <i className="bi bi-calendar3"></i>
      </button>

      {open && (
        <div className="rr-datepicker-popup">
          <div className="rr-datepicker-header">
            <button
              type="button"
              className="rr-datepicker-nav"
              onClick={goPrevMonth}
              disabled={isCurrentMonth}
              aria-label="Önceki ay"
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <span className="rr-datepicker-title">{MONTHS[month]} {year}</span>
            <button type="button" className="rr-datepicker-nav" onClick={goNextMonth} aria-label="Sonraki ay">
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>

          <div className="rr-datepicker-weekdays">
            {WEEKDAYS.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>

          <div className="rr-datepicker-grid">
            {cells.map((d, i) => {
              if (!d) return <span key={`empty-${i}`} className="rr-datepicker-cell empty" />;
              const disabled = d.getTime() < today.getTime();
              const isSelected = Boolean(selected && toISO(d) === toISO(selected));
              const isToday = toISO(d) === toISO(today);
              return (
                <button
                  key={toISO(d)}
                  type="button"
                  className={`rr-datepicker-cell${isSelected ? " selected" : ""}${isToday ? " today" : ""}`}
                  disabled={disabled}
                  onClick={() => selectDay(d)}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
